import { useState } from 'react';
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  Paragraph,
  Radio,
  RadioGroup,
  Stack,
  useSnackbars,
  type PlumaManagedModalProps,
} from '@customerio/pluma-components/react';
import type { Customer } from '@/services/db/customer/entity';
import { useSetCustomersEmailTrackingConsent } from '@/services/db/customer/mutations';

export interface PeopleOpenTrackingConsentModalData {
  workspaceId: number;
  profiles: ReadonlyArray<Pick<Customer, 'id' | 'attributes'>>;
  /** "N selected people" — composed by the actions bar. */
  peopleCountLabel: string;
}

/**
 * Sets the per-contact email tracking consent attribute on the selected
 * profiles. Granted contacts have their email opens tracked; denied contacts
 * do not. Confirms (clearing the selection and reloading the list) on full or
 * partial success; stays open on total failure so the user can retry.
 */
export const PeopleOpenTrackingConsentModal = ({
  data,
  onConfirm,
  onClose,
  modalState,
}: PlumaManagedModalProps<PeopleOpenTrackingConsentModalData>) => {
  const { workspaceId, profiles, peopleCountLabel } = data;
  const snackbars = useSnackbars();
  const [selectedConsent, setSelectedConsent] = useState<'consented' | 'denied'>('consented');

  const setConsent = useSetCustomersEmailTrackingConsent({ workspaceId });
  const isSaving = setConsent.isPending;

  const apply = () => {
    if (isSaving) return;
    void (async () => {
      const { total, failed } = await setConsent.mutateAsync({
        profiles,
        consent: selectedConsent === 'denied' ? 'denied' : 'granted',
      });

      if (failed === 0) {
        snackbars.create({
          variant: 'default',
          id: 'people.list.open-tracking-consent.success',
          message: `Email tracking consent updated for ${peopleCountLabel}.`,
        });
        onConfirm();
      } else if (failed === total) {
        snackbars.create({
          variant: 'critical',
          id: 'people.list.open-tracking-consent.failure',
          message: `Email tracking consent could not be updated. Try again or contact support.`,
        });
      } else {
        snackbars.create({
          variant: 'critical',
          id: 'people.list.open-tracking-consent.partial',
          message: `Updated ${total - failed} of ${total} people. ${failed} failed. Try again for the rest.`,
        });
        onConfirm();
      }
    })();
  };

  return (
    <Modal
      data-test-people-open-tracking-consent-modal
      state={modalState}
      title="Set email tracking consent"
      onClose={onClose}
      isDismissable={!isSaving}
    >
      <ModalBody>
        <Stack gap="150">
          <Paragraph>
            Set the email tracking consent preference for {peopleCountLabel}. Consenting contacts have their email opens
            tracked; denied contacts do not.
          </Paragraph>

          <RadioGroup
            label="Email tracking consent"
            value={selectedConsent}
            onChange={(value) => setSelectedConsent(value === 'denied' ? 'denied' : 'consented')}
            isDisabled={isSaving}
          >
            <Radio data-test-open-tracking-consent-option="consented" value="consented" label="Granted" />
            <Radio data-test-open-tracking-consent-option="denied" value="denied" label="Denied" />
          </RadioGroup>
        </Stack>
      </ModalBody>

      <ModalFooter>
        <Button variant="tertiary" onClick={() => onClose()} isDisabled={isSaving}>
          Cancel
        </Button>
        <Button data-test-open-tracking-consent-apply variant="primary" isLoading={isSaving} onClick={apply}>
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
};
